import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { refreshBlobUrls, useFetchData, useLoadImageWithAuth } from './apiHooks';
import { API_ENDPOINTS } from 'constants/index.js';
import parse from 'html-react-parser';
import DOMPurify from "dompurify";
import TextWithLineBreaks from 'components/Editor/TextWithLineBreaks';
import CalendarEvent from 'components/modules/email/CalendarEvent';
import ErrorBoundary from 'components/common/ErrorBoundary';
import { useNavigate } from 'react-router-dom';
import { OBJ } from 'constants';

export function useFetchEmailOnSelection(selectedItem, refetch, email) {
  useEffect(() => {
    if (selectedItem && email && (!email.isLoading && email.id !== selectedItem.id)) {
        console.log("refetch in useFetchEmailOnSelection", selectedItem, email)
      refetch();
    }
  }, [selectedItem, refetch, email]);
}

export function useMarkEmailAsRead(email, handleEmailChange) {
    const hasMarkedRead = useRef(false);
    useEffect(() => {
        if (email && !hasMarkedRead.current) {
            if(!email.is_read){
                handleEmailChange('is_read', true);
            }
            hasMarkedRead.current = true;
        }
    }, [email, handleEmailChange]);
}

export function useFetchRelatedEmails(email, emailToRespond, fetchEmailToRespond) {
  useEffect(() => {
    if (email?.replied_to_email_id && !emailToRespond) {
      fetchEmailToRespond();
    }
  }, [email, emailToRespond, fetchEmailToRespond]);
}

export const useHtmlEmail = (email) => {
    return useMemo(() => {
        if (!email?.body) return <div></div>;
        if (email.content_type === 'text/plain' && typeof email.body === 'string') 
            return <TextWithLineBreaks text={email.body}/>
        if (email.content_type === 'text/calendar') {
            return <CalendarEvent icsData={email.body} />
        }    
        return parse(DOMPurify.sanitize(email?.body, {
            ALLOWED_URI_REGEXP: /^(?:(?:(?:https?|blob|data):)|(?:[^:/?#]*(?:[/?#]|$)))/i,
            ADD_ATTR: ['data-backend-id', 'filename'],
        }))   
    }, [email?.body, email?.content_type]);
};

export const useNewEmailDraft = () => {
    const navigate = useNavigate();
    const { data, error: errorSignature, refetch } = useFetchData(API_ENDPOINTS.EMAILS.SIGNATURES.DEFAULT,null,true);
    const { saveData: createEmail, saveError: errorCreateEmail } = useFetchData(API_ENDPOINTS.EMAILS.LIST, null, false);

    const createDraft = async (contactId = null, recipients) => {
        let signature = data?.content || ''
        if(!data){
            signature = await refetch()
            signature = signature.content || ''
        }
        console.log(data, signature)

        return {
            body: signature,
            status: 'draft',
            is_read: true,
            contact_id: contactId || undefined,
            recipients: recipients
        };
    };

    const createNewEmailDraft = async (newLine, contactId, recipients) => {
        const draft = await createDraft(contactId, recipients);
        Object.assign(newLine, draft);
        return newLine
    };

    const createNewEmail = async (contactId, recipients) => {
        const newEmailDraft = await createNewEmailDraft({ id: -1 }, contactId, recipients)
        return await createEmail({items: [newEmailDraft]})
    };

    const createAndOpenEmail = async (contactId, emailAddress) => {
        const newEmail = await createNewEmail(contactId, [emailAddress]);
        console.log(newEmail)
        navigate(`/admin/${OBJ.EMAILS}/${newEmail[0].id}`, { replace: false });
    }

    return { standardSignature: data?.content || '', createDraft, createNewEmailDraft, createNewEmail, errorSignature, errorCreateEmail, createAndOpenEmail };
};

export function useUpdateImages(email, divRef) {
    const loadImageWithAuth = useLoadImageWithAuth();

    useEffect(() => {
        if (email?.body && divRef.current) {
            updateImage();  
        }
    }, [email]);

    async function updateImage(){
        await refreshBlobUrls(divRef.current, loadImageWithAuth);
    }
}

const EmailContentRenderer = ({ email }) => { 
    const divRef = useRef(null);
    const htmlEmail = useHtmlEmail(email);
    useUpdateImages(email, divRef);

    return (
        <ErrorBoundary fallback={<div>An error occurred in the HTML Email</div>}>
            <div ref={divRef} className="table-responsive">
                {htmlEmail}
            </div>
        </ErrorBoundary>
    );
};

export default EmailContentRenderer;